<!-- 实勘详情页的交易所 -->
<script setup lang="ts">
const { t, locale, localePath } = useAppI18n()
/** 当前语言是否为中文，用于决定中英文冒号展示。 */
const isChinese = computed(() => isChineseLocale(locale))
/** 中英文场景下使用不同的冒号符号。 */
const chinesColon = computed(() => (isChinese.value ? '：' : ': '))
const SCORE_ICON = 'https://resources1.interface003.com/web30/2026/06/16/5c03a3c1-306a-474f-9267-2f40fd9d866b.png'
type TraderLabelDataItem = {
  labelName?: string
}

type TraderLabel = {
  type?: number
  data?: TraderLabelDataItem[] | null
  Data?: TraderLabelDataItem[] | null
}

type TraderContract = {
  contract?: string
  type?: number
}

type TraderInfo = {
  logo?: string
  ico?: string
  flag?: string
  shortName?: string
  localShortName?: string
  fullName?: string | null
  localFullName?: string
  companyName?: string
  country?: string
  countryName?: string
  annotation?: string
  color?: string
  operatingYears?: string
  labels?: TraderLabel[] | null
  contracts?: TraderContract[] | null
  facebook?: string
  twitter?: string
  contactPhone?: string
  alias?: string
  code?: string
  showName?: string
  localName?: string
  defaultName?: string
  score?: number | string
  [key: string]: unknown
}

/** 组件入参，交易商侧栏信息由父组件透传。 */
const props = defineProps<{
  info?: TraderInfo | null
}>()

/** 文本缺省占位。 */
const DEFAULT_VALUE = '--'
/** 联系方式类型: 电话。 */
const CONTACT_TYPE_PHONE = 1
/** 联系方式类型: Facebook。 */
const CONTACT_TYPE_FACEBOOK = 2
/** 联系方式类型: 邮箱。 */
const CONTACT_TYPE_EMAIL = 3
/** 联系方式类型: X。 */
const CONTACT_TYPE_X = 5

/** 对交易商信息做空值兜底，方便模板安全读取。 */
const info = computed(() => props.info ?? {})

/** 交易商 Logo，优先使用 logo，缺失时回退 ico。 */
const logoUrl = computed(() => info.value.ico)

/** 交易商简称展示文案。 */
const traderName = computed(
  () => info.value?.showName || info.value?.defaultName || info.value?.localName || DEFAULT_VALUE,
)

const alias = computed(() => info.value?.alias || info.value?.code || '')

/** 监管或实勘状态文案。 */
const statusText = computed(() => info.value.annotation || DEFAULT_VALUE)

/** 从主字段或标签字段中提取经营年限。 */
const tradingYears = computed(() => {
  if (info.value.operatingYears) {
    return info.value.operatingYears
  }

  const labels = info.value.labels ?? []
  const yearLabel = labels.find((item) => item.type === 101)
  const labelData = yearLabel?.data?.[0]?.labelName || yearLabel?.Data?.[0]?.labelName

  return labelData || DEFAULT_VALUE
})

/** 将联系方式数组整理成按类型索引的 Map，便于复用。 */
const contactMap = computed(() => {
  const map = new Map<number, string>()

  for (const item of info.value.contracts ?? []) {
    if (item.type && item.contract && !map.has(item.type)) {
      map.set(item.type, item.contract)
    }
  }

  return map
})

/** 公司全称展示字段。 */
const companyFullName = computed(
  () => info.value.companyName || info.value.localFullName || info.value.fullName || DEFAULT_VALUE,
)

const traderScore = computed(() => info.value?.score)

/** 公司注册地区展示字段。 */
const registeredRegion = computed(() => info.value.countryName || info.value.country || DEFAULT_VALUE)

/** 官方邮箱。 */
const officialEmail = computed(() => contactMap.value.get(CONTACT_TYPE_EMAIL) || DEFAULT_VALUE)

/** X 社媒链接。 */
const xLink = computed(() => info.value.twitter || contactMap.value.get(CONTACT_TYPE_X) || DEFAULT_VALUE)

/** Facebook 社媒链接。 */
const facebookLink = computed(() => info.value.facebook || contactMap.value.get(CONTACT_TYPE_FACEBOOK) || DEFAULT_VALUE)

/** 客服电话。 */
const customerPhone = computed(
  () => info.value.contactPhone || contactMap.value.get(CONTACT_TYPE_PHONE) || DEFAULT_VALUE,
)

/** 详情区字段配置，模板按此顺序渲染。 */
const detailRows = computed(() => [
  {
    label: t('82570') + chinesColon.value,
    value: companyFullName.value,
  },
  {
    label: t('84065') + chinesColon.value,
    value: traderName.value,
  },
  {
    label: t('84066') + chinesColon.value,
    value: registeredRegion.value,
  },
  {
    label: t('82529'),
    value: officialEmail.value,
  },
  {
    label: 'X' + chinesColon.value,
    value: xLink.value,
  },
  {
    label: 'Facebook' + chinesColon.value,
    value: facebookLink.value,
  },
  {
    label: t('82531'),
    value: customerPhone.value,
  },
])

const route = useRoute()
const { setBreadcrumb } = useLsBreadcrumb()
const handleClick = () => {
  setBreadcrumb(alias.value, {
    labelKey: '84048', // 走翻译的值
    route: route.fullPath,
  })
}
</script>

<template>
  <div>
    <aside
      class="flex w-full min-w-0 flex-col items-center gap-[20px] rounded-[12px] border border-[rgba(11,18,40,0.14)] bg-white p-[24px] max-[1000px]:hidden"
    >
      <!-- "/exchanges/{exchange_slug}" -->
      <NuxtLink
        class="group flex w-full flex-col items-center gap-[20px] border-b-[0.5px] border-solid border-[rgba(5,21,36,0.06)]"
        :to="localePath({ name: 'exchange-detail', params: { slug: alias } })"
        target="_blank"
        @click="handleClick"
      >
        <div class="size-[76px] overflow-hidden rounded-[8px] bg-[#f5f7fb]">
          <CommonImage :src="logoUrl" alt="trader logo" class="block size-full object-cover" loading="lazy" />
        </div>

        <div :class="isChinese ? 'gap-[12px]' : 'gap-[8px]'" class="flex w-full flex-col items-center pb-[20px]">
          <p class="w-full text-center text-[24px] leading-[32px] font-[600] text-[#000] group-hover:text-[#0D3F63]">
            {{ traderName }}
          </p>

          <div class="flex w-full items-center justify-center gap-[8px]">
            <div
              class="flex items-center gap-[4px] rounded-[2px] border-[0.5px] border-[rgba(5,21,36,0.06)] bg-[rgba(255,255,255,0.4)] px-[8px] py-[2px]"
            >
              <CommonImage
                v-if="info.flag"
                :src="info.flag"
                :alt="registeredRegion"
                class="h-[12px] w-[18px] rounded-[1px] object-cover"
                loading="lazy"
              />
              <span class="text-[13px] leading-[20px] font-[400] text-[#292a2e]">
                {{ tradingYears }}
              </span>
            </div>

            <div
              v-if="statusText && info.color"
              class="flex items-center rounded-[2px] px-[8px] py-[2px] text-white"
              :style="
                info.color
                  ? {
                      backgroundColor: String(info.color),
                    }
                  : undefined
              "
            >
              <span class="text-[13px] leading-[20px] font-[400] text-white">
                {{ statusText }}
              </span>
            </div>
          </div>
        </div>
      </NuxtLink>

      <div class="flex w-full flex-col gap-[12px] text-[14px] leading-[22px]">
        <div
          v-for="item in detailRows"
          :key="item.label"
          class="flex items-start"
          :class="isChinese ? 'flex-row gap-[8px]' : 'flex-col gap-[2px]'"
        >
          <span class="shrink-0 whitespace-nowrap text-[#7d818a]">
            {{ item.label }}
          </span>
          <span class="min-w-0 flex-1 text-right font-[500] break-words text-[#292a2e]">
            {{ item.value || DEFAULT_VALUE }}
          </span>
        </div>
      </div>
    </aside>

    <div class="hidden border-t-[0.5px] border-solid border-[rgba(5,21,36,0.06)] pt-[4px] max-[1000px]:flex">
      <NuxtLink
        to=""
        target="_blank"
        class="flex w-full cursor-pointer items-center gap-[8px] py-[12px] hover:bg-[rgba(0,0,0,0.03)]"
      >
        <div class="trader-logo size-[32px] shrink-0 overflow-hidden rounded-[4px] bg-[#f5f7fb]">
          <CommonImage
            :src="logoUrl"
            class="block size-full object-cover"
            loading="eager"
            :alt="'trader logo' + traderName"
          />
        </div>

        <div class="min-w-0 flex-1">
          <div class="flex items-center justify-between gap-[8px]">
            <div class="min-w-0 flex-1">
              <div class="flex items-center gap-[4px]">
                <p class="truncate text-[16px] leading-[24px] font-[500] text-[#292a2e]">
                  {{ traderName }}
                </p>
              </div>

              <div class="mt-[2px] flex flex-wrap items-center gap-[8px]">
                <span
                  v-if="statusText"
                  class="text-[11px] leading-[16px] font-[400]"
                  :style="{
                    color: info.color,
                  }"
                >
                  {{ statusText }}
                </span>

                <span class="h-[8px] w-[1px] bg-[rgba(5,21,36,0.06)]" />

                <span class="text-[11px] leading-[16px] font-[400] text-[#505258]">
                  {{ tradingYears }}
                </span>
              </div>
            </div>

            <div class="inline-flex shrink-0 items-center gap-[4px] rounded-[1000px] bg-[#f8f8f8] px-[12px] py-[4px]">
              <CommonImage :src="SCORE_ICON" alt="score icon" class="size-[14px] shrink-0" loading="eager" />
              <span class="text-center text-[16px] leading-[24px] font-[500] text-[#292a2e]">
                {{ traderScore }}
              </span>
            </div>
          </div>
        </div>
      </NuxtLink>
    </div>
  </div>
</template>
